import { useFormik } from "formik";
import * as Yup from "yup";
import { useContext, useEffect, useMemo, useState } from "react";
import RadioButton from "../../../Helpers/RadioButton/RadioButton";
import { Input } from "../../../Helpers/Input/Input";
import { InputSelect } from "../../../Helpers/Input/RefactoredInput";
import trans from "../../../../utils";
import {
  useCreateRegularMaintenanceMutation,
  useEditRegularMaintenanceMutation,
  useGetRegularMaintenanceByTraccarIdQuery,
} from "../../../../Redux/service/Maintenance/MaintenanceNew";
import { toast } from "react-toastify";
import { popupcontext } from "../../../../context/Popupscontext";
import Button from "../../../Helpers/Button/Button";

const options = [
  { label: "Low", value: "Low" },
  { label: "Medium", value: "Medium" },
  { label: "High", value: "High" },
  { label: "Highest", value: "Highest" },
];

const typeOptions = [
  { label: trans("Hours"), value: "hours" },
  { label: trans("Odometer"), value: "odometer" },
];

const RegularFormBody = ({ isEditing, allData }) => {
  const { currentMaintenanceId, setEdited, handleClose } =
    useContext(popupcontext);
  const [submitMaintenance] = useCreateRegularMaintenanceMutation();
  const [editMaintenance] = useEditRegularMaintenanceMutation();

  const { data: editData } = useGetRegularMaintenanceByTraccarIdQuery(
    currentMaintenanceId,
    {
      skip: !isEditing || !currentMaintenanceId,
      refetchOnMountOrArgChange: true,
    },
  );
  const selected = editData?.data;

  const getNextCode = () => {
    const codes = allData
      ?.map((item) => item.code || "")
      .filter((code) => code.startsWith("MA-"));
    const lastNumber = Math.max(
      ...codes.map((code) => parseInt(code.split("-")[1] || 0)),
    );
    const nextNumber = lastNumber + 1;
    return `MA-${String(nextNumber).padStart(3, "0")}`;
  };

  const initialValues = useMemo(() => {
    if (isEditing && selected) {
      return {
        name: selected.name,
        type: selected.type,
        period: Math.round(selected.period),
        start: Math.round(selected.start) ?? "",
        description: selected.description || "",
        priority: selected.priority || "",
      };
    }
    return {
      name: "",
      type: "",
      period: "",
      start: "",
      description: "",
      priority: "",
    };
  }, [isEditing, selected]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .strict(true)
        .matches(/^(?!\s).+$/, "Name cannot start with a space")
        .min(2, "Name must be at least 2 characters")
        .trim()
        .required("Required"),
      type: Yup.string().required("Required"),
      period: Yup.number()
        .typeError("Must be a number")
        .required("Required")
        .min(1),
      start: Yup.number()
        .typeError("Must be a number")
        .required("Required")
        .min(1),
      priority: Yup.string().required("Required"),
      description: Yup.string(),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      const createPayload = {
        name: values.name,
        type: values.type,
        start: values.start,
        period: values.period,
        attributes: {
          description: values.description,
          priority: values.priority,
          code: getNextCode(),
        },
      };

      const editPayload = {
        name: values.name,
        type: values.type,
        start: values.start,
        period: values.period,
        attributes: {
          description: values.description,
          priority: values.priority,
        },
      };

      try {
        setSubmitting(true);
        if (isEditing) {
          await editMaintenance({
            id: currentMaintenanceId,
            body: editPayload,
          }).unwrap();
          toast.success(trans("Maintenance updated successfully!"), {
            autoClose: 800,
            rtl: localStorage.getItem("lang") === "ar" ? true : false,
          });
        } else {
          await submitMaintenance({ body: createPayload }).unwrap();
          toast.success(trans("Maintenance added successfully!"), {
            autoClose: 800,
            rtl: localStorage.getItem("lang") === "ar" ? true : false,
          });
        }
        resetForm();
        handleClose("form");
        setEdited(true);
      } catch (error) {
        toast.error("Submission error:", error, {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    handleClose("form");
  };

  return (
    <>
      <form className="h-form" onSubmit={formik.handleSubmit}>
        <div className="userinfo mb-24">
          <Input
            title="Maintenance Name"
            type="text"
            placeholder="Name"
            formstyle="user-form w-100 mb-16"
            id="name"
            name="name"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            errors={formik.errors.name}
            touched={formik.touched.name}
            value={formik.values.name}
          />
          <div className="flex flex-row gap-16 w-100 mb-16">
            <InputSelect
              id="type"
              name="type"
              label="Type"
              placeholder="Select type"
              options={typeOptions}
              value={formik.values.type}
              onChange={(val) => {
                formik.setFieldValue("type", val ?? "");
                if (!val) formik.setFieldTouched("type", true);
              }}
              onBlur={() => formik.setFieldTouched("type", true)}
              errors={formik.errors.type}
              touched={formik.touched.type}
              formstyle="w-100"
            />
            <Input
              disabled={!formik.values.type}
              title={
                formik.values.type === "odometer"
                  ? "Maintenance Distance"
                  : "Maintenance Hours"
              }
              type="number"
              placeholder="10000"
              formstyle="user-form w-100 flex-1"
              id="period"
              name="period"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              errors={formik.errors.period}
              touched={formik.touched.period}
              value={formik.values.period}
            />
          </div>
          <div className="flex flex-row gap-16 w-100 mb-16">
            <Input
              disabled={!formik.values.type}
              title={
                formik.values.type === "odometer"
                  ? "Start Distance"
                  : "Start Hours"
              }
              type="number"
              placeholder="1"
              formstyle="user-form w-100 flex-1"
              id="start"
              name="start"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              errors={formik.errors.start}
              touched={formik.touched.start}
              value={formik.values.start}
            />
          </div>
          <Input
            title="Description"
            type="text"
            placeholder="Optional Description"
            formstyle="user-form w-100 mb-16"
            id="description"
            name="description"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            errors={formik.errors.description}
            touched={formik.touched.description}
            value={formik.values.description}
          />
          <RadioButton
            title={"Priority"}
            formstyle="user-form"
            options={options}
            value={formik.values.priority}
            setValue={(val) => formik.setFieldValue("priority", val)}
            errors={formik.errors.priority}
            touched={formik.touched.priority}
          />
        </div>
        <div className="offcanvas-footer m-0 flex-between">
          <Button
            type={"default"}
            htmlType="button"
            onClick={handleCancel}
            disabled={formik.isSubmitting}
          >
            {trans("Cancel")}
          </Button>
          <Button
            type={"primary"}
            htmlType="submit"
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? trans("Saving...") : trans("Save")}
          </Button>
        </div>
      </form>
    </>
  );
};

export default RegularFormBody;
